<template>
  <div class="flex flex-col gap-2">
    <slot name="header" />
    <div class="field-shell h-8 gap-2 px-2.5" @click.stop>
      <IconSearch class="size-3.5 flex-none text-text-muted" />
      <input
        ref="inputRef"
        type="text"
        name="search-box"
        :value="search"
        :placeholder="placeholder"
        class="field-input flex-1"
        @input="onSearch($event.target.value)"
        @keydown.up.prevent="listRef.selectPrev()"
        @keydown.down.prevent="listRef.selectNext()"
        @keydown.enter="onEnter"
      >
      <span
        v-if="search"
        class="flex size-4 flex-none cursor-pointer items-center justify-center
               text-text-muted transition-colors duration-150 hover:text-accent"
        :title="$t('search.clear')"
        @click.stop="onSearch('')"
      >
        <IconX class="size-3.5" />
      </span>
    </div>
    <AppItemsList
      v-show="filtered.length"
      ref="listRef"
      :items="filtered"
      :getLabel="getLabel"
      :horizontal="horizontal"
      :closeOnSelect="closeOnSelect"
      @select="onSelect"
    >
      <template v-if="$slots.item" #item="slotProps">
        <slot name="item" v-bind="slotProps" />
      </template>
    </AppItemsList>
    <slot v-if="!filtered.length" name="empty" :query="search.trim()" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { IconSearch, IconX } from '@iconify-prerendered/vue-tabler';
import AppItemsList from '@/components/elements/AppItemsList';
import { filterItems } from '@/utils';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  getLabel: {
    type: Function,
    default: (item) => item.label,
  },
  // text the search query is matched against, defaults to the label
  getSearchText: {
    type: Function,
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  autofocus: {
    type: Boolean,
    default: true,
  },
  horizontal: {
    type: Boolean,
    default: false,
  },
  closeOnSelect: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select', 'enterQuery']);

const search = ref('');
const inputRef = ref(null);
const listRef = ref(null);

const filtered = computed(
  () => filterItems(props.items, search.value, props.getSearchText || props.getLabel),
);

function onSearch(value) {
  search.value = value;
  listRef.value.resetSelection();
}

defineExpose({ clear: () => onSearch('') });

onMounted(() => {
  if (props.autofocus) setTimeout(() => inputRef.value.focus());
});

function onEnter() {
  if (!props.horizontal && filtered.value.length) {
    listRef.value.selectByKeyboard();
    return;
  }
  const query = search.value.trim();
  if (query) emit('enterQuery', query);
}

function onSelect(item) {
  if (props.closeOnSelect) search.value = '';
  emit('select', item);
}
</script>
